import { ColDef, GridApi, IFilterOptionDef } from '@ag-grid-community/core'
import {
  BaseFilterComponent,
  FilterOptionDef,
  SimpleFilterOptions,
} from '@northfork/ag-grid-angular'
import _ from 'lodash-es'
import { FilterOperator } from '../models/filter-operator.model'

/**
 * Get operators for a given ag-grid column definition
 */
export function getOperators(
  colDef: ColDef,
  gridApi: GridApi,
): FilterOperator[] {
  if (!colDef.field) {
    return []
  }

  const availableOptions = getAvailableFilterOptions(colDef.field, gridApi)
  const filterOptions = getFilterOptions(colDef.field, gridApi)
  const filterOperators = []

  for (const option of filterOptions) {
    if (typeof option === 'string') {
      if (availableOptions.has(option)) {
        filterOperators.push(
          getOperatorFromFilterOption(
            availableOptions.get(option) as FilterOptionDef<string>,
          ),
        )
      } else {
        filterOperators.push(getOperatorFromKey(option))
      }
    } else {
      filterOperators.push(getOperatorFromFilterOption(option))
    }
  }

  return filterOperators
}

/**
 * Get available filter options from the filter component instance
 * of a given field.
 *
 * This is a map with information about filter operators, and is useful
 * because it includes customized filter operator display names for a given field.
 */
export function getAvailableFilterOptions(
  field: string,
  gridApi: GridApi,
): Map<string, FilterOptionDef<string>> {
  const filterInstance = gridApi.getFilterInstance(field)

  if (!filterInstance) {
    return new Map()
  }

  if ('setFilterParams' in filterInstance) {
    return new Map([
      [
        'in',
        {
          displayKey: 'in',
          displayName: 'In',
        } as FilterOptionDef<string>,
      ],
    ])
  }

  const filterComponentInstance =
    filterInstance.getFrameworkComponentInstance?.()
  const availableTypes: Map<
    string,
    FilterOptionDef<string>
  > = filterComponentInstance?.availableTypes ?? new Map()

  return availableTypes
}

/**
 * Get available filter operator types from the filter component instance
 * of a given field.
 */
export function getFilterOptions(
  field: string,
  gridApi: GridApi,
): (string | FilterOptionDef<string>)[] {
  const filterInstance = gridApi.getFilterInstance(field)

  if (!filterInstance) {
    return []
  }

  if ('setFilterParams' in filterInstance) {
    return ['in']
  }

  const filterComponentInstance: BaseFilterComponent<
    SimpleFilterOptions<string>,
    unknown,
    unknown
  > | null = filterInstance.getFrameworkComponentInstance?.()
  const filterOptions: (string | FilterOptionDef<string>)[] =
    filterComponentInstance?.params?.filterOptions ?? []

  return filterOptions
}

/**
 * Build a FilterOperator from an ag-grid IFilterOptionDef.
 */
export function getOperatorFromFilterOption(
  opt: IFilterOptionDef,
): FilterOperator {
  return {
    type: opt.displayKey,
    name: opt.displayName,
  }
}

/**
 * Build a FilterOperator object from an operator key string.
 *
 * This simply converts an operator key from camel case to start case.
 * However, often an operator has a display name this is different word
 * than the key. So use this function tactically.
 */
export function getOperatorFromKey(key: string): FilterOperator {
  return { type: key, name: _.startCase(key) }
}
